<script setup lang="ts">
import { onBeforeMount, ref } from "vue";
import ChatOnboarding from "@/features/chat-onboarding/ChatOnboarding.vue";
import DataDeletePageBackground from "@/features/data-delete/DataDeletePageBackground.vue";
import { fetchFeatureFlag } from "@/composables/usePostHogFeatureFlag";
import { useMonitoringAutofill } from "@/features/monitoring/useMonitoringAutofill";
import { toApiPayload } from "@/features/enrollment/data-utils";
import DataDeleteService from "@/api/actions/data-delete-service";
import type { EnrollmentRequest } from "@/routes/enrollmentV2/PageExposureStatusEnrollExposures.vue";
import type {
  ChatOnboardingFeatureFlag,
  ChatScript,
  Profile,
} from "@/features/chat-onboarding/types.ts";
import store from "@/store";
import EmailService from "@/api/actions/email-service";

const script = ref<ChatScript>([]);

onBeforeMount(async () => {
  const { payload }: ChatOnboardingFeatureFlag = await fetchFeatureFlag(
    "post_checkout_chatbot_experience"
  );

  script.value = payload ?? [];
});

const getFreshProfile = (): Profile => ({
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
  address: null,
  dateOfBirth: null,
});

const {
  combineAutofillData,
  getAutofillDataFromEnrollment,
  getAutofillDataFromSearchResultsV2,
  getAutofillDataFromUserObject,
} = useMonitoringAutofill();

type AutofillData = Partial<{
  name: {
    first: string;
    middle: string;
    last: string;
  };
  dob: string;
  email_addresses: string[];
  phone_numbers: string[];
  addresses: {
    address1: string;
    city: string;
    state: string;
    postal_code: string;
  }[];
}>;

const profile = ref<Profile>(getFreshProfile());

onBeforeMount(async () => {
  let newProfile: AutofillData = {};

  const dataFromSearchResults = await getAutofillDataFromSearchResultsV2();
  newProfile = await combineAutofillData(newProfile, dataFromSearchResults);

  const dataFromUserObject = await getAutofillDataFromUserObject();
  newProfile = await combineAutofillData(newProfile, dataFromUserObject);

  const dataFromEnrollment = await getAutofillDataFromEnrollment();
  newProfile = await combineAutofillData(newProfile, dataFromEnrollment);

  profile.value = {
    firstName: newProfile.name?.first ?? null,
    lastName: newProfile.name?.last ?? null,
    email: newProfile.email_addresses?.[0] ?? null,
    phone: newProfile.phone_numbers?.[0] ?? null,
    dateOfBirth: newProfile.dob ?? null,
    address: null, // ignore address autofill for now
  };
});

const onComplete = async () => {
  const rawPayload: EnrollmentRequest = {
    name: {
      first: profile.value.firstName ?? undefined,
      last: profile.value.lastName ?? undefined,
    },
    dob: profile.value.dateOfBirth ?? undefined,
    addresses: profile.value.address ? [profile.value.address] : undefined,
    email_addresses: profile.value.email ? [profile.value.email] : undefined,
    phone_numbers: profile.value.phone ? [profile.value.phone] : undefined,
    ssn: undefined,
  };

  try {
    await DataDeleteService.createEnrollmentData(toApiPayload(rawPayload));
  } catch (error) {
    // letting API silently fail in this flow
    console.error(error);
  }
};

const onEmailConfirmed = async (email: string) => {
  try {
    await EmailService.addEmail({
      email,
      primary: true,
      collection_name: "email",
      collection: store.getters["authentication/collection"]("email"),
      user: store.state.authentication?.user?.url,
    });
  } catch (error) {
    // letting API silently fail in this flow
    console.error(error);
  }
};
</script>

<template>
  <div class="chat-onboarding-page">
    <ChatOnboarding
      v-if="script.length > 0"
      v-model:profile="profile"
      :script="script"
      class="chat-onboarding-page__chat"
      @complete="onComplete"
      @email-confirmed="onEmailConfirmed"
    />
    <DataDeletePageBackground />
  </div>
</template>

<style lang="scss" scoped>
.chat-onboarding-page {
  margin: 0 auto;
  width: 100%;

  &__chat {
    position: relative;
    z-index: 2;
  }
}
</style>
